import React, { useState } from 'react';
import { useAction, useQuery } from 'wasp/client/operations';
import { createBuySell, getBuySellFormMode, getSimplifiedFormEnabled, getOrganizationBySlug } from 'wasp/client/operations';
import { SimplifiedSubmitForm, SubmissionModeSwitch } from '../components/SimplifiedSubmit';
import { useAuth } from 'wasp/client/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../../client/components/Sidebar';
import { useLocationContext } from '../../client/contexts/LocationContext';
import { locationSectionPathFromLocation } from '../../client/lib/locationPaths';
import {
  ArrowLeft,
  ArrowRight,
  ShoppingCart,
  DollarSign,
  Phone,
  Save,
  AlertCircle,
  HelpCircle,
  Star,
  Package,
  CheckCircle2,
  FileText,
  User,
} from 'lucide-react';

interface BuySellFormData {
  title: string;
  description: string;
  price: string;
  category: 'BUY' | 'SELL';
  condition: 'NEW' | 'LIKE_NEW' | 'GOOD' | 'FAIR' | 'POOR';
  contactInfo: string;
  visibility: 'PUBLIC' | 'SUBSCRIBER' | 'VERIFIED';
}

const BUY_SELL_CATEGORIES = [
  { value: 'SELL', label: 'Want to Sell', description: 'I have something to offer', color: 'green', icon: Package },
  { value: 'BUY', label: 'Want to Buy', description: 'I am looking for something', color: 'blue', icon: ShoppingCart }
] as const;

const CONDITION_OPTIONS = [
  { value: 'NEW', label: 'New', description: 'Brand new, never used', emoji: '✨' },
  { value: 'LIKE_NEW', label: 'Like New', description: 'Excellent condition, barely used', emoji: '🌟' },
  { value: 'GOOD', label: 'Good', description: 'Good condition, some wear', emoji: '👍' },
  { value: 'FAIR', label: 'Fair', description: 'Fair condition, noticeable wear', emoji: '🛠️' },
  { value: 'POOR', label: 'Poor', description: 'Poor condition, significant wear', emoji: '🔧' }
] as const;

// ─── Tooltip ─────────────────────────────────────────────────────────────────
const Tooltip = ({ children, content }: { children: React.ReactNode; content: string }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative inline-block">
      <div onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)} className="cursor-help">
        {children}
      </div>
      {visible && (
        <div className="absolute z-10 px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64">
          {content}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
};

// ─── Step Progress Bar ────────────────────────────────────────────────────────
interface StepConfig {
  id: number;
  label: string;
  icon: React.ElementType;
  description: string;
}

const STEPS: StepConfig[] = [
  { id: 1, label: 'Posting Type', icon: ShoppingCart, description: 'What are you posting?' },
  { id: 2, label: 'Item Details', icon: Package, description: 'Describe your item' },
  { id: 3, label: 'Pricing', icon: DollarSign, description: 'Set a price' },
  { id: 4, label: 'Contact & Review', icon: User, description: 'Finalize and submit' }
];

const StepIndicator = ({ currentStep }: { currentStep: number }) => (
  <div className="w-full mb-8">
    {/* Progress bar */}
    <div className="relative flex items-center justify-between">
      {/* Connecting line */}
      <div className="absolute top-4 left-0 right-0 h-0.5 bg-gray-200">
        <div
          className="h-full bg-blue-500 transition-all duration-500 ease-out"
          style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
        />
      </div>

      {STEPS.map((step) => {
        const Icon = step.icon;
        const isDone = currentStep > step.id;
        const isCurrent = currentStep === step.id;
        return (
          <div key={step.id} className="relative flex flex-col items-center z-10">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                isDone
                  ? 'bg-blue-500 border-blue-500 text-white'
                  : isCurrent
                  ? 'bg-white border-blue-500 text-blue-600 shadow-md shadow-blue-100'
                  : 'bg-white border-gray-200 text-gray-400'
              }`}
            >
              {isDone ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
            </div>
            <span
              className={`mt-2 text-xs font-medium hidden sm:block ${
                isCurrent ? 'text-blue-600' : isDone ? 'text-blue-400' : 'text-gray-400'
              }`}
            >
              {step.label}
            </span>
          </div>
        );
      })}
    </div>

    {/* Current step description */}
    <div className="mt-4 text-center">
      <p className="text-sm text-gray-500">
        Step {currentStep} of {STEPS.length} —{' '}
        <span className="font-medium text-gray-700">{STEPS[currentStep - 1].description}</span>
      </p>
    </div>
  </div>
);

// ─── Single-Page Form (legacy) ────────────────────────────────────────────────
function SinglePageForm({
  formData,
  errors,
  isSubmitting,
  handleChange,
  handleSubmit,
  handleGoBack,
  selectedLocationName
}: {
  formData: BuySellFormData;
  errors: Record<string, string>;
  isSubmitting: boolean;
  handleChange: (field: keyof BuySellFormData, value: any) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleGoBack: () => void;
  selectedLocationName: string;
}) {
  return (
    <div className="flex h-full">
      {/* Form Content */}
      <div className="flex-1 p-6 pb-32 lg:pb-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <ShoppingCart className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Post Buy/Sell Item</h1>
              <p className="text-gray-600">Location: {selectedLocationName}</p>
            </div>
          </div>
        </div>

        <form id="buy-sell-form" onSubmit={handleSubmit}>
          <div className="space-y-6 mb-6">
            {/* Category */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">What are you posting?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {BUY_SELL_CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <label
                      key={cat.value}
                      className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                        formData.category === cat.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="category"
                        value={cat.value}
                        checked={formData.category === cat.value}
                        onChange={(e) => handleChange('category', e.target.value as 'BUY' | 'SELL')}
                        className="sr-only"
                      />
                      <div className="flex items-center gap-3">
                        <Icon className={`w-6 h-6 ${formData.category === cat.value ? 'text-blue-600' : 'text-gray-400'}`} />
                        <div>
                          <div className="font-medium text-gray-900">{cat.label}</div>
                          <div className="text-sm text-gray-500">{cat.description}</div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Item Details</h3>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.title ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="e.g., MacBook Pro 13-inch, Vintage Camera, Office Chair"
                required
              />
              {errors.title && (
                <p className="mt-1 text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.title}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Description</h3>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={4}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors ${
                  errors.description ? 'border-destructive bg-destructive/10' : 'border-input'
                }`}
                placeholder="Provide detailed information about the item, condition, reason for selling, etc..."
                required
              />
              {errors.description && (
                <p className="text-destructive text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.description}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              {/* Condition */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Condition</label>
                <select
                  value={formData.condition}
                  onChange={(e) => handleChange('condition', e.target.value as BuySellFormData['condition'])}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                >
                  {CONDITION_OPTIONS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              {/* Price */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <div className="flex items-center gap-2">
                    <span>Price</span>
                    <Tooltip content="Leave empty if you prefer to negotiate or if it's free">
                      <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Contact */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  <div className="flex items-center gap-2">
                    <span>Contact Information</span>
                    <Tooltip content="How buyers/sellers can reach you">
                      <HelpCircle className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </Tooltip>
                  </div>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={formData.contactInfo}
                    onChange={(e) => handleChange('contactInfo', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-input rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent transition-colors"
                    placeholder="Phone number or email"
                  />
                </div>
              </div>
            </div>
          </div>

          {errors.submit && (
            <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {errors.submit}
            </div>
          )}
        </form>
      </div>

      {/* Right Sidebar */}
      <div className="hidden lg:flex w-80 border-l border-border flex-col">
        <div className="p-6 border-b border-border">
          <div className="flex flex-col gap-3">
            <button
              type="submit"
              form="buy-sell-form"
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.title.trim()}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium"
            >
              {isSubmitting ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Posting...</>
              ) : (
                <><Save className="w-4 h-4" />Submit</>
              )}
            </button>
            <button
              type="button"
              onClick={handleGoBack}
              className="w-full px-4 py-3 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium"
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </div>
        <div className="p-6 border-b border-border">
          <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>All postings go through an approval process before being published.</span>
          </div>
        </div>
        <div className="p-6 flex-1">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Tips for Better Results</h3>
          <div className="space-y-3 text-sm text-gray-600">
            {[
              'Use clear, descriptive titles that include key details',
              'Include detailed descriptions and honest condition assessment',
              'Set a fair price based on current market value',
              'Provide accurate contact information for easy communication',
              'Be responsive to inquiries to increase your chances of success'
            ].map((tip) => (
              <div key={tip} className="flex items-start gap-2">
                <Star className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <span>{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Wizard Step Components ───────────────────────────────────────────────────

function Step1Category({ formData, handleChange }: { formData: BuySellFormData; handleChange: (f: keyof BuySellFormData, v: any) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">What are you posting?</h2>
        <p className="text-gray-500 text-sm">Choose whether you're looking to buy or sell an item.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        {BUY_SELL_CATEGORIES.map((cat) => {
          const selected = formData.category === cat.value;
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => handleChange('category', cat.value)}
              className={`relative flex flex-col items-center justify-center p-3 sm:p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200 ${
                selected
                  ? cat.value === 'SELL'
                    ? 'border-green-500 bg-gradient-to-br from-green-50 to-emerald-50 shadow-md shadow-green-100'
                    : 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-md shadow-blue-100'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-sm bg-white'
              }`}
            >
              <div className="text-center">
                <div className={`text-sm sm:text-lg font-bold mb-0.5 sm:mb-1 ${selected ? (cat.value === 'SELL' ? 'text-green-700' : 'text-blue-700') : 'text-gray-700'}`}>
                  {cat.label}
                </div>
                <div className="text-xs sm:text-sm text-gray-500">{cat.description}</div>
              </div>
              {selected && (
                <div className={`absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center ${cat.value === 'SELL' ? 'bg-green-500' : 'bg-blue-500'}`}>
                  <CheckCircle2 className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Title prompt here so step 1 captures both category AND title */}
      <div className="pt-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Item Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          className="w-full px-4 py-3 border border-input rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-gray-900 placeholder-gray-400"
          placeholder="e.g., MacBook Pro 13-inch, Vintage Camera, Office Chair"
        />
        <p className="mt-1.5 text-xs text-gray-400">A short, clear title helps people find your post quickly.</p>
      </div>
    </div>
  );
}

function Step2Details({ formData, errors, handleChange }: { formData: BuySellFormData; errors: Record<string, string>; handleChange: (f: keyof BuySellFormData, v: any) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Describe your item</h2>
        <p className="text-gray-500 text-sm">Give potential buyers or sellers all the information they need.</p>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Description <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => {
            handleChange('description', e.target.value);
            e.target.style.height = 'auto';
            e.target.style.height = `${e.target.scrollHeight}px`;
          }}
          rows={4}
          maxLength={255}
          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none overflow-hidden ${
            errors.description ? 'border-red-300 bg-red-50' : 'border-input'
          }`}
          placeholder="Describe the item in detail — size, colour, brand, why you're selling, any defects, etc..."
        />
        {errors.description && (
          <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" /> {errors.description}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-1.5">{formData.description.length}/255 characters</p>
      </div>

      {/* Condition */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Condition</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CONDITION_OPTIONS.map((cond) => (
            <button
              key={cond.value}
              type="button"
              onClick={() => handleChange('condition', cond.value)}
              className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all duration-150 ${
                formData.condition === cond.value
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div>
                <div className={`text-sm font-semibold ${formData.condition === cond.value ? 'text-blue-700' : 'text-gray-700'}`}>
                  {cond.label}
                </div>
                <div className="text-xs text-gray-400">{cond.description}</div>
              </div>
              {formData.condition === cond.value && (
                <CheckCircle2 className="w-4 h-4 text-blue-500 ml-auto flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step3Pricing({ formData, handleChange }: { formData: BuySellFormData; handleChange: (f: keyof BuySellFormData, v: any) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Set your price</h2>
        <p className="text-gray-500 text-sm">Leave the price empty if you'd like to negotiate or it's free.</p>
      </div>

      <div className="relative max-w-xs">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium text-lg">$</div>
        <input
          type="number"
          step="1"
          min="0"
          value={formData.price}
          onChange={(e) => handleChange('price', e.target.value)}
          className="w-full pl-10 pr-6 py-4 text-2xl font-semibold border-2 border-input rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          placeholder="0"
        />
      </div>

      {/* Quick price hints */}
      <div className="flex flex-wrap gap-2">
        {['Free', '5', '10', '25', '50', '100'].map((hint) => (
          <button
            key={hint}
            type="button"
            onClick={() => handleChange('price', hint === 'Free' ? '' : hint)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              (hint === 'Free' && formData.price === '') || formData.price === hint
                ? 'bg-blue-500 text-white border-blue-500'
                : 'border-gray-200 text-gray-600 hover:border-gray-400 bg-white'
            }`}
          >
            {hint === 'Free' ? 'Free' : `$${hint}`}
          </button>
        ))}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-700">
          <strong>Tip:</strong> Setting a fair market price attracts more interest and leads to faster transactions.
        </div>
      </div>
    </div>
  );
}

function Step4Review({
  formData,
  errors,
  isSubmitting,
  handleChange,
  handleSubmit
}: {
  formData: BuySellFormData;
  errors: Record<string, string>;
  isSubmitting: boolean;
  handleChange: (f: keyof BuySellFormData, v: any) => void;
  handleSubmit: (e: React.FormEvent) => void;
}) {
  const categoryLabel = BUY_SELL_CATEGORIES.find((c) => c.value === formData.category)?.label ?? formData.category;
  const conditionOption = CONDITION_OPTIONS.find((c) => c.value === formData.condition);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Contact &amp; Review</h2>
        <p className="text-gray-500 text-sm">Add your contact info and confirm everything looks good before submitting.</p>
      </div>

      {/* Contact */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Contact Information
        </label>
        <input
          type="text"
          value={formData.contactInfo}
          onChange={(e) => handleChange('contactInfo', e.target.value)}
          className="w-full px-4 py-3 border border-input rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
          placeholder="Phone number or email"
        />
        <p className="mt-1.5 text-xs text-gray-400">Pre-filled from your account — edit if needed.</p>
      </div>

      {/* Summary card */}
      <div className="rounded-2xl border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-semibold text-gray-700">Posting Summary</span>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Type</span>
            <span className="font-medium text-gray-900">{categoryLabel}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Title</span>
            <span className="font-medium text-gray-900 max-w-[60%] text-right">{formData.title || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Condition</span>
            <span className="font-medium text-gray-900">{conditionOption?.emoji} {conditionOption?.label}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Price</span>
            <span className="font-medium text-gray-900">
              {formData.price ? `$${parseInt(formData.price, 10)}` : 'Negotiable / Free'}
            </span>
          </div>
          {formData.description && (
            <div className="pt-2 border-t border-gray-100">
              <span className="text-gray-500 block mb-1">Description</span>
              <p className="text-gray-700 text-xs leading-relaxed line-clamp-3">{formData.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Approval notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">
          Your posting will go through an <strong>approval process</strong> before it is published to other community members.
        </p>
      </div>

      {errors.submit && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {errors.submit}
        </div>
      )}
    </div>
  );
}

// ─── Wizard Wrapper ───────────────────────────────────────────────────────────
function WizardForm({
  formData,
  errors,
  isSubmitting,
  handleChange,
  handleSubmit,
  handleGoBack,
  selectedLocationName
}: {
  formData: BuySellFormData;
  errors: Record<string, string>;
  isSubmitting: boolean;
  handleChange: (field: keyof BuySellFormData, value: any) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleGoBack: () => void;
  selectedLocationName: string;
}) {
  const [step, setStep] = useState(1);
  const TOTAL = STEPS.length;

  const canAdvanceFrom1 = formData.title.trim().length > 0;
  const canAdvanceFrom2 = formData.description.trim().length > 0;

  const handleNext = () => {
    if (step === 1 && !canAdvanceFrom1) return;
    if (step === 2 && !canAdvanceFrom2) return;
    setStep((s) => Math.min(s + 1, TOTAL));
  };

  const handleBack = () => {
    if (step === 1) {
      handleGoBack();
    } else {
      setStep((s) => Math.max(s - 1, 1));
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <ShoppingCart className="w-7 h-7 text-blue-600 flex-shrink-0" />
        <div>
          <h1 className="text-xl font-bold text-gray-900">Post Buy/Sell Item</h1>
          <p className="text-sm text-gray-500">Location: {selectedLocationName}</p>
        </div>
      </div>

      {/* Step indicator */}
      <StepIndicator currentStep={step} />

      {/* Step content (animated) */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 min-h-[360px]">
        <div className="transition-all duration-300">
          {step === 1 && (
            <Step1Category formData={formData} handleChange={handleChange} />
          )}
          {step === 2 && (
            <Step2Details formData={formData} errors={errors} handleChange={handleChange} />
          )}
          {step === 3 && (
            <Step3Pricing formData={formData} handleChange={handleChange} />
          )}
          {step === 4 && (
            <Step4Review
              formData={formData}
              errors={errors}
              isSubmitting={isSubmitting}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
            />
          )}
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between mt-6 gap-3">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-2 px-5 py-2.5 text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          {step === 1 ? 'Cancel' : 'Back'}
        </button>

        {step < TOTAL ? (
          <button
            type="button"
            onClick={handleNext}
            disabled={(step === 1 && !canAdvanceFrom1) || (step === 2 && !canAdvanceFrom2)}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors font-medium text-sm"
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => handleSubmit(e as any)}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
          >
            {isSubmitting ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Posting...</>
            ) : (
              <><CheckCircle2 className="w-4 h-4" />Submit Posting</>
            )}
          </button>
        )}
      </div>

      {/* Step mini-nav dots */}
      <div className="flex justify-center gap-2 mt-4">
        {STEPS.map((s) => (
          <div
            key={s.id}
            className={`transition-all duration-300 rounded-full ${
              s.id === step
                ? 'w-6 h-2 bg-blue-500'
                : s.id < step
                ? 'w-2 h-2 bg-blue-300'
                : 'w-2 h-2 bg-gray-200'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AddBuySellPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const { data: org } = useQuery(getOrganizationBySlug, { slug: slug! }, { enabled: !!slug }) as { data: any };

  const createBuySellFn = useAction(createBuySell);
  const { data: formMode, isLoading: formModeLoading } = useQuery(getBuySellFormMode);
  const { data: simplifiedEnabled, isLoading: simplifiedEnabledLoading } = useQuery(getSimplifiedFormEnabled);

  const [submissionMode, setSubmissionMode] = useState<'full' | 'simplified'>('simplified');

  const defaultContact = [
    (user as any)?.phoneNumber,
    user?.email
  ].filter(Boolean).join(' · ');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<BuySellFormData>({
    title: '',
    description: '',
    price: '',
    category: 'SELL',
    condition: 'GOOD',
    contactInfo: defaultContact,
    visibility: 'VERIFIED'
  });

  const handleChange = (field: keyof BuySellFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!selectedLocation?.id) newErrors.location = 'No location selected';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await createBuySellFn({
        title: formData.title.trim(),
        description: formData.description.trim(),
        locationId: selectedLocation!.id,
        organizationId: org?.id,
        price: formData.price ? parseInt(formData.price, 10) : undefined,
        category: formData.category,
        condition: formData.condition,
        contactInfo: formData.contactInfo.trim(),
        visibility: formData.visibility,
        submissionType: 'STANDARD'
      });
      handleGoBack();
    } catch (error) {
      console.error('Error creating buy/sell item:', error);
      setErrors({ submit: 'Failed to create item. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSimplifiedSubmit = async (description: string) => {
    if (!selectedLocation?.id) {
      setErrors({ submit: 'No location selected.' });
      return;
    }
    setIsSubmitting(true);
    try {
      const shortTitle = description.length > 60 ? description.slice(0, 57) + '…' : description;
      await createBuySellFn({
        title: shortTitle,
        description,
        locationId: selectedLocation.id,
        category: 'SELL',
        condition: 'GOOD',
        contactInfo: defaultContact,
        visibility: 'VERIFIED',
        submissionType: 'QUICK'
      });
      handleGoBack();
    } catch (error) {
      console.error('Error creating simplified buy/sell item:', error);
      setErrors({ submit: 'Failed to submit. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(locationSectionPathFromLocation(selectedLocation, 'buy-sell'));
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You must be logged in to post buy/sell items.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isWizard = !formModeLoading && formMode === 'wizard';
  const locationName = selectedLocation?.name || 'No location selected';

  const showModeSwitch = !!simplifiedEnabled;
  const effectiveMode = showModeSwitch ? submissionMode : 'full';

  return (
    <DashboardLayout>
      <div className="flex h-full">
        <div className="flex-1 bg-card">
          {formModeLoading || simplifiedEnabledLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : effectiveMode === 'simplified' ? (
            /* ── Simplified form ───────────────────────────────────────── */
            <div className="overflow-y-auto h-full pb-32 lg:pb-6 bg-gray-50">
              {showModeSwitch && (
                <SubmissionModeSwitch
                  activeMode={submissionMode}
                  onSelectMode={setSubmissionMode}
                />
              )}
              <SimplifiedSubmitForm
                isSubmitting={isSubmitting}
                onSubmit={handleSimplifiedSubmit}
                selectedLocationName={locationName}
              />
            </div>
          ) : isWizard ? (
            /* ── Multi-step wizard ─────────────────────────────────────── */
            <div className="overflow-y-auto h-full pb-32 lg:pb-6 bg-gray-50">
              {showModeSwitch && (
                <SubmissionModeSwitch
                  activeMode={submissionMode}
                  onSelectMode={setSubmissionMode}
                />
              )}
              <WizardForm
                formData={formData}
                errors={errors}
                isSubmitting={isSubmitting}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                handleGoBack={handleGoBack}
                selectedLocationName={locationName}
              />
            </div>
          ) : (
            /* ── Single-page form ──────────────────────────────────────── */
            <>
              {showModeSwitch && (
                <SubmissionModeSwitch
                  activeMode={submissionMode}
                  onSelectMode={setSubmissionMode}
                />
              )}
              <SinglePageForm
                formData={formData}
                errors={errors}
                isSubmitting={isSubmitting}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                handleGoBack={handleGoBack}
                selectedLocationName={locationName}
              />
            </>
          )}
        </div>
      </div>

      {/* Mobile bottom bar (single-page mode only) */}
      {effectiveMode === 'full' && !isWizard && (
        <div className="fixed bottom-14 left-0 right-0 z-10 lg:hidden bg-card border-t border-border px-4 py-3 shadow-lg">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleGoBack}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="buy-sell-form"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm flex items-center justify-center gap-2"
            >
              {isSubmitting ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> : 'Post'}
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}



